/**
 * Tough-Bar Builder - Main Entry Point
 * Combines all modules into the Alpine.js component
 */
window.ToughBarBuilder = window.ToughBarBuilder || {};

const REQUIRED_BUILDER_MODULES = {
  state: module => typeof module === 'function',
  api: module => module && typeof module === 'object',
  vehicle: module => module && typeof module === 'object',
  device: module => module && typeof module === 'object',
  pricing: module => module && typeof module === 'object',
  modals: module => module && typeof module === 'object',
  navigation: module => module && typeof module === 'object',
  accessories: module => module && typeof module === 'object'
};

function getMissingBuilderModules(builderModules) {
  return Object.entries(REQUIRED_BUILDER_MODULES)
    .filter(([moduleName, isValid]) => !isValid(builderModules?.[moduleName]))
    .map(([moduleName]) => moduleName);
}

document.addEventListener('alpine:init', () => {
  const builderModules = window.ToughBarBuilder || {};
  const missingModules = getMissingBuilderModules(builderModules);

  if (missingModules.length > 0) {
    console.error(
      `[Tough-Bar Builder] Missing required modules at Alpine init: ${missingModules.join(', ')}. Check script order in sections/tough-bar-builder.liquid.`
    );
    return;
  }

  Alpine.data('toughBarBuilder', function() {
    return {
      // ===================
      // State (from tough-bar-state.js)
      // ===================
      ...builderModules.state(),

      // ===================
      // API Methods (from tough-bar-api.js)
      // ===================
      ...builderModules.api,

      // ===================
      // Vehicle Methods (from tough-bar-vehicle.js)
      // ===================
      ...builderModules.vehicle,

      // ===================
      // Device Methods (from tough-bar-device.js)
      // ===================
      ...builderModules.device,

      // ===================
      // Pricing Methods (from tough-bar-pricing.js)
      // ===================
      ...builderModules.pricing,

      // ===================
      // Modal Methods (from tough-bar-modals.js)
      // ===================
      ...builderModules.modals,

      // ===================
      // Navigation Methods (from tough-bar-navigation.js)
      // ===================
      ...builderModules.navigation,

      // ===================
      // Accessories Methods (from tough-bar-accessories.js)
      // ===================
      ...builderModules.accessories,

      // ===================
      // Side Cart Methods (from tough-bar-side-cart.js)
      // ===================
      ...(builderModules.sideCart || {}),

      // ===================
      // Computed Properties (getters must be defined here)
      // ===================

      // Step Info
      get step() {
        return this.response.step || 1;
      },
      get visibleStep() {
        return Math.min(Number(this.step || 1), 3);
      },
      get stepName() {
        return this.response.step_name || 'vehicle';
      },
      get hasSummaryKitData() {
        return Array.isArray(this.response?.kits) && this.response.kits.length > 0;
      },
      get isFinalKitState() {
        return Boolean(
          this.context?.accessories_complete === true
          || Number(this.step || 1) >= 4
          || (this.visibleStep === 3 && this.hasSummaryKitData)
        );
      },
      setAccessoriesComplete(isComplete = true) {
        if (!this.context || typeof this.context !== 'object') {
          this.context = {};
        }

        if (isComplete) {
          this.context.accessories_complete = true;
          return;
        }

        delete this.context.accessories_complete;
      },

      // Vehicle (Step 1)
      get options() {
        return this.response.section?.options || {};
      },
      get trimOptions() {
        return this.response.section?.trim_options || {};
      },
      get years() {
        return Object.keys(this.options).sort((a, b) => b - a);
      },
      get makes() {
        if (!this.context.year) return [];
        return Object.keys(this.options[this.context.year] || {}).sort();
      },
      get models() {
        if (!this.context.year || !this.context.make) return [];
        const models = this.options[this.context.year]?.[this.context.make] || [];
        return [...models].sort();
      },
      get trims() {
        if (!this.context.year || !this.context.make || !this.context.model) return [];
        const trims = this.trimOptions[this.context.year]?.[this.context.make]?.[this.context.model] || [];
        return [...trims].sort();
      },
      get hasTrimOptions() {
        return this.trims.length > 0;
      },

      // Device (Step 2)
      get deviceButtons() {
        return this.response.section?.buttons || [];
      },
      get toughBar() {
        // Tough bar data is now consolidated into kit.tough_bar
        return this.kit.tough_bar || {};
      },

      // Image Stacking
      imageLayerOrder: {
        'Keyboard': 0.5,  // Above tablet case and tablet mount
        'Case': 1,
        'Dock': 2,
        'Mount': 4,
        'Laptop': 5,
        'Tough-Bar': 6,
        'Environment': 7   // Furthest back layer
      },
      environmentImagesByStep: {
        2: 'https://images.rammount.com/images/builders/tough-bar/Backgrounds/environment_1.png',
        3: 'https://images.rammount.com/images/builders/tough-bar/Backgrounds/environment_2.png',
        4: 'https://images.rammount.com/images/builders/tough-bar/Backgrounds/environment_3.png'
      },
      get environmentImageUrl() {
        const stepNumber = Number(this.step || 1);
        return this.environmentImagesByStep?.[stepNumber] || '';
      },
      get kit() {
        const responseKit = this.response.kit || {};
        // localKit is Step 2-only staging state before the next API transition.
        if ((this.step || 1) !== 2) return responseKit;

        const localKit = (this.localKit && typeof this.localKit === 'object') ? this.localKit : {};
        const deviceTypes = ['tablet', 'laptop', 'other_device'];
        const hasLocalDeviceKit = deviceTypes.some(deviceType => Object.prototype.hasOwnProperty.call(localKit, deviceType));

        if (!hasLocalDeviceKit) {
          return { ...responseKit, ...localKit };
        }

        // When Step 2 selection changes (for example tablet -> laptop),
        // clear stale device kits from response state so image/cart reflect only the active device kit.
        const nextKit = { ...responseKit };
        deviceTypes.forEach(deviceType => {
          if (Object.prototype.hasOwnProperty.call(localKit, deviceType)) {
            nextKit[deviceType] = localKit[deviceType];
            return;
          }
          nextKit[deviceType] = { products: [], images: [] };
        });

        return nextKit;
      },
      get stackedImages() {
        const images = [];
        const environmentImageUrl = this.environmentImageUrl;

        if (environmentImageUrl) {
          images.push({
            url: environmentImageUrl,
            layer: this.imageLayerOrder.Environment ?? 7,
            alt: 'Tough-Bar environment'
          });
        }

        // Tough bar images (now an array in kit.tough_bar.images)
        if (this.toughBar.images?.length > 0) {
          this.toughBar.images.forEach(imageUrl => {
            images.push({
              url: imageUrl,
              layer: this.getImageLayer(imageUrl),
              alt: 'Tough-Bar'
            });
          });
        }
        // Get images from kit (API returns kit at root level)
        const deviceTypes = ['laptop', 'tablet', 'other_device'];
        deviceTypes.forEach(deviceType => {
          const deviceKit = this.kit[deviceType];
          if (deviceKit && deviceKit.images && Array.isArray(deviceKit.images)) {
            deviceKit.images.forEach(imageUrl => {
              images.push({
                url: imageUrl,
                layer: this.getImageLayer(imageUrl),
                alt: deviceType
              });
            });
          }
        });
        return images
          .sort((a, b) => b.layer - a.layer)
          .map((image, index) => ({
            ...image,
            zIndex: index + 1
          }));
      },

      // Accessories (Step 3)
      get defaultVisibleAccessoryButtonCount() {
        return 4;
      },
      getAccessoryStepSortPriority(button) {
        if (!button || typeof button !== 'object') return Number.MAX_SAFE_INTEGER;

        const toTokens = value => String(value || '')
          .trim()
          .toLowerCase()
          .split(/[^a-z0-9]+/)
          .filter(Boolean);

        const tokens = [
          ...toTokens(button.id),
          ...toTokens(button.label)
        ];

        const hasTokens = (...expectedTokens) => expectedTokens.every(token => tokens.includes(token));

        if (tokens.includes('phone')) return 0;
        if (tokens.includes('printer')) return 1;
        if ((typeof this.isKeyboardAccessoryButton === 'function' && this.isKeyboardAccessoryButton(button)) || tokens.includes('keyboard')) return 1.5;
        if (tokens.includes('gps')) return 2;
        if (tokens.includes('cupholder') || hasTokens('cup', 'holder')) return 3;
        return Number.MAX_SAFE_INTEGER;
      },
      get accessoryButtons() {
        const currentButtons = this.response.section?.buttons;
        const sourceButtons = (Array.isArray(currentButtons) && currentButtons.length > 0)
          ? currentButtons
          : (Array.isArray(this.accessoryButtonsCache) ? this.accessoryButtonsCache : []);

        if (sourceButtons.length <= 1) return sourceButtons;

        return sourceButtons
          .map((button, index) => ({
            button,
            index,
            priority: this.getAccessoryStepSortPriority(button)
          }))
          .sort((a, b) => {
            if (a.priority !== b.priority) return a.priority - b.priority;
            return a.index - b.index;
          })
          .map(entry => entry.button);
      },
      get visibleAccessoryButtons() {
        const buttons = Array.isArray(this.accessoryButtons) ? this.accessoryButtons : [];
        return typeof this.getAccessoryButtonsWithKeyboardOption === 'function'
          ? this.getAccessoryButtonsWithKeyboardOption(buttons)
          : buttons;
      },
      get hasHiddenAccessoryButtons() {
        return false;
      },
      showAllAccessoryButtons() {
        this.accessoryButtonsExpanded = true;
      },
      get maxAccessories() {
        return this.response.section?.max_selections || 3;
      },
      get countableSelectedAccessories() {
        const accessories = Array.isArray(this.selectedAccessories) ? this.selectedAccessories : [];
        const countableAccessories = accessories.filter(accessory => {
          return !(typeof this.isKeyboardAccessoryButton === 'function' && this.isKeyboardAccessoryButton(accessory?.id));
        });
        const keyboardAccessory = typeof this.getSelectedKeyboardAccessory === 'function'
          ? this.getSelectedKeyboardAccessory()
          : null;

        return keyboardAccessory
          ? [...countableAccessories, keyboardAccessory]
          : countableAccessories;
      },
      get selectedAccessoryCount() {
        return this.countableSelectedAccessories.length;
      },
      get canSelectMoreAccessories() {
        return this.selectedAccessoryCount < this.maxAccessories;
      },
      get hasAccessoryLoading() {
        return this.countableSelectedAccessories.some(accessory => accessory?.loading);
      },
      get hasBlockingAccessoryError() {
        return this.countableSelectedAccessories.some(accessory => this.isAccessoryErrorBlocking(accessory));
      },
      get accessoryKitSlots() {
        // Return array of 3 slots, filled with selected accessories or empty
        const accessories = this.countableSelectedAccessories;
        const slots = [];
        for (let i = 0; i < this.maxAccessories; i++) {
          slots.push(accessories[i] || null);
        }
        return slots;
      },

      // Accessory Modal computed properties
      get currentAccessoryButton() {
        const activeAccessoryId = this.modal?.accessoryId || this.accessoryModal.accessoryId || this.accessoryCustomizeTarget;
        if (!activeAccessoryId) return null;
        return this.getAccessoryButton(activeAccessoryId);
      },
      get accessoryModalOptions() {
        return this.getAccessoryPrimarySelectionMeta(this.currentAccessoryButton).options || {};
      },
      get accessoryModalFields() {
        return this.getAccessoryPrimarySelectionMeta(this.currentAccessoryButton).fields || [];
      },
      get accessoryMakes() {
        const options = Object.keys(this.accessoryModalOptions).sort();
        const selected = this.accessoryModal.make;
        if (selected && !options.includes(selected)) {
          return [selected, ...options];
        }
        return options;
      },
      get accessoryModels() {
        if (!this.accessoryModal.make) return [];
        const isPhoneAccessory = (() => {
          const button = this.currentAccessoryButton;
          const tokens = [
            String(button?.id || ''),
            String(button?.label || '')
          ]
            .join(' ')
            .trim()
            .toLowerCase()
            .split(/[^a-z0-9]+/)
            .filter(Boolean);

          return tokens.includes('phone');
        })();
        const sortDirection = isPhoneAccessory ? -1 : 1;
        const sortOptions = options => [...options].sort((left, right) => {
          const normalizedLeft = String(left || '').toLowerCase();
          const normalizedRight = String(right || '').toLowerCase();
          return normalizedLeft.localeCompare(normalizedRight) * sortDirection;
        });
        const makeData = this.accessoryModalOptions[this.accessoryModal.make];
        // Handle different data structures (array vs object)
        if (Array.isArray(makeData)) {
          const options = sortOptions(makeData);
          const selected = this.accessoryModal.model;
          if (selected && !options.includes(selected)) {
            return [selected, ...options];
          }
          return options; // Simple array of models
        }
        const options = sortOptions(Object.keys(makeData || {}));
        const selected = this.accessoryModal.model;
        if (selected && !options.includes(selected)) {
          return [selected, ...options];
        }
        return options;
      },
      get accessoryCaseTypes() {
        if (!this.accessoryModal.make || !this.accessoryModal.model) return [];
        const makeData = this.accessoryModalOptions[this.accessoryModal.make];
        if (Array.isArray(makeData)) {
          return []; // No case types for simple array structure
        }
        const options = makeData?.[this.accessoryModal.model] || [];
        const selected = this.accessoryModal.case_type;
        if (selected && !options.includes(selected)) {
          return [selected, ...options];
        }
        return options;
      },
      get hasCaseTypeField() {
        return this.accessoryModalFields.some(f => f.key === 'case_type');
      },
      get canConfirmAccessory() {
        if (!this.accessoryModal.make) return false;
        // Check if model is required and selected
        const needsModel = this.accessoryModels.length > 0;
        if (needsModel && !this.accessoryModal.model) return false;
        // Check if case_type is required and selected
        if (this.hasCaseTypeField && this.accessoryCaseTypes.length > 0 && !this.accessoryModal.case_type) return false;
        return true;
      },

      // Tablet Modal
      get tabletModal() {
        const tabletButton = this.deviceButtons.find(b => b.id === 'tablet');
        return tabletButton?.modal || {};
      },
      get tabletOptions() {
        const options = this.tabletModal.options || {};
        const cached = this.tabletOptionsCache || {};
        const resolved = Object.keys(options).length > 0 ? options : cached;
        if (Object.keys(resolved).length > 0) return resolved;
        if (this.context.tablet_make && this.context.tablet_model && this.context.tablet_case_type) {
          return {
            [this.context.tablet_make]: {
              [this.context.tablet_model]: [this.context.tablet_case_type]
            }
          };
        }
        return {};
      },
      get tabletMakes() {
        const options = Object.keys(this.tabletOptions).sort();
        const selected = this.context.tablet_make;
        if (selected && !options.includes(selected)) {
          return [selected, ...options];
        }
        return options;
      },
      get tabletModels() {
        if (!this.context.tablet_make) return [];
        const options = Object.keys(this.tabletOptions[this.context.tablet_make] || {}).sort();
        const selected = this.context.tablet_model;
        if (selected && !options.includes(selected)) {
          return [selected, ...options];
        }
        return options;
      },
      get tabletCaseTypes() {
        if (!this.context.tablet_make || !this.context.tablet_model) return [];
        const options = this.tabletOptions[this.context.tablet_make]?.[this.context.tablet_model] || [];
        const selected = this.context.tablet_case_type;
        if (selected && !options.includes(selected)) {
          return [selected, ...options];
        }
        return options;
      },
      get canConfirmTablet() {
        const make = this.context.tablet_make;
        const model = this.context.tablet_model;
        const caseType = this.context.tablet_case_type;
        if (!(make && model && caseType)) return false;
        return this.tabletCaseTypes.includes(caseType);
      },
      get tabletDisplaySelection() {
        const fromContext = {
          tablet_make: this.context.tablet_make || null,
          tablet_model: this.context.tablet_model || null,
          tablet_case_type: this.context.tablet_case_type || null
        };
        const fromConfirmed = this.tabletSelectionConfirmed || {};
        const fromBackup = typeof this.getTabletSelectionDisplayBackup === 'function'
          ? this.getTabletSelectionDisplayBackup()
          : (this.tabletSelectionBackup || {});
        const isTabletSelectionStagedInModal = typeof this.isTabletSelectionStagedInOpenModal === 'function'
          ? this.isTabletSelectionStagedInOpenModal()
          : (this.modal?.open && this.modal?.type === 'tablet');
        const isComplete = selection => Boolean(
          selection?.tablet_make &&
          selection?.tablet_model &&
          selection?.tablet_case_type
        );

        // Keep Step 2 card stable while editing in modal.
        if (isTabletSelectionStagedInModal) {
          if (isComplete(fromBackup)) return fromBackup;
          if (isComplete(fromConfirmed)) return fromConfirmed;
          return fromBackup;
        }

        if (isComplete(fromConfirmed)) return fromConfirmed;
        return fromContext;
      },
      get tabletDisplayValue() {
        const selection = this.tabletDisplaySelection || {};
        const parts = [];
        if (selection.tablet_make) parts.push(selection.tablet_make);
        if (selection.tablet_model) parts.push(selection.tablet_model);
        if (parts.length > 0 && selection.tablet_case_type) {
          return `${parts.join(' ')} - ${selection.tablet_case_type}`;
        }
        return parts.join(' ');
      },
      get tabletDisplaySubtitle() {
        const selection = this.tabletDisplaySelection || {};
        const parts = [];
        if (selection.tablet_make) parts.push(selection.tablet_make);
        if (selection.tablet_model) parts.push(selection.tablet_model);
        return parts.join(' ');
      },

      // Navigation
      get canProceed() {
        switch (this.step) {
          case 1:
            if (!(this.context.year && this.context.make && this.context.model)) {
              return false;
            }

            if (!this.hasTrimOptions) {
              return true;
            }

            return this.trims.includes(this.context.trim_level);
          case 2:
            // Can proceed if a device is actually selected
            return (
              this.isDeviceSelected('tablet') ||
              this.isDeviceSelected('laptop') ||
              this.isDeviceSelected('other_device')
            );
          case 3:
            return !this.hasAccessoryLoading && !this.hasBlockingAccessoryError;
          default:
            return true;
        }
      },
      get showPricing() {
        return this.step > 1;
      },

      // Pricing Bar
      get cartItems() {
        const items = [];
        // Tough bar products (now an array in kit.tough_bar.products)
        if (this.toughBar.products?.length > 0) {
          this.toughBar.products.forEach(handle => {
            const product = this.getCachedProductByHandle(handle);
            if (product) {
              items.push({
              handle: handle,
              product: product,
              section: 'tough-bar'
              });
            }
          });
        }
        // Get products from kit (API returns kit at root level)
        const deviceTypes = ['laptop', 'tablet', 'other_device'];
        deviceTypes.forEach(deviceType => {
          const deviceKit = this.kit[deviceType];
          if (deviceKit && deviceKit.products && Array.isArray(deviceKit.products)) {
            deviceKit.products.forEach(handle => {
              const product = this.getCachedProductByHandle(handle);
              if (product) {
                items.push({
                handle: handle,
                product: product,
                section: 'device',
                deviceType: deviceType
                });
              }
            });
          }
        });
        // Add accessory products
        this.selectedAccessories.forEach(accessory => {
          if (accessory.productData) {
            items.push({
              handle: accessory.productData.handle,
              product: accessory.productData,
              section: 'accessory',
              accessoryId: accessory.id
            });
          }
        });
        return items
          .map(item => {
            const stateKey = this.buildCartItemStateKey(item);
            const nextItem = { ...item, stateKey };

            return {
              ...nextItem,
              quantity: this.getQuantity(nextItem)
            };
          })
          .filter(item => !this.isRemoved(item));
      },
      get printKitItems() {
        const products = new Map();
        this.cartItems.forEach(item => {
          if (this.isExcluded(item)) return;
          const product = item.product;
          const variant = product.variants?.[0];
          const key = String(variant?.id || item.handle);
          const existing = products.get(key);
          if (existing) {
            existing.quantity += item.quantity;
            return;
          }
          let image = product.featured_image || product.images?.[0] || '';
          if (image) {
            try {
              const imageUrl = new URL(image, window.location.origin);
              if (imageUrl.hostname === 'cdn.shopify.com' || imageUrl.pathname.startsWith('/cdn/shop/')) {
                // Full-resolution originals made a 10-item printout roughly 25 MB.
                imageUrl.searchParams.set('width', '180');
                if (imageUrl.hostname === 'cdn.shopify.com') imageUrl.protocol = 'https:';
                image = imageUrl.href;
              }
            } catch (_error) {
              // Keep non-URL image sources unchanged.
            }
          }
          // Include selected out-of-stock products; printing describes the whole kit.
          // Project only display fields so prices cannot enter the print template.
          products.set(key, {
            key,
            title: product.title || item.handle,
            sku: variant?.sku || item.handle.toUpperCase(),
            image,
            quantity: item.quantity
          });
        });
        return Array.from(products.values());
      },
      get availableCartItems() {
        return this.cartItems.filter(item =>
          item.product?.available && !this.isExcluded(item)
        );
      },
      get toughBarItems() {
        return this.cartItems.filter(item => item.section === 'tough-bar');
      },
      get hasToughBarSelection() {
        return this.toughBarItems.length > 0;
      },
      get deviceItems() {
        return this.cartItems.filter(item => item.section === 'device');
      },
      get hasDeviceSelection() {
        return this.deviceItems.length > 0;
      },
      get accessoryItems() {
        return this.cartItems.filter(item => item.section === 'accessory');
      },
      get hasAccessorySelection() {
        return this.accessoryItems.length > 0;
      },
      getIncludedQuantity(items = []) {
        return items.reduce((sum, item) => {
          if (!item?.product?.available || this.isExcluded(item)) return sum;
          return sum + (item.quantity || 1);
        }, 0);
      },
      get toughBarCount() {
        return this.getIncludedQuantity(this.toughBarItems);
      },
      get deviceCount() {
        return this.getIncludedQuantity(this.deviceItems);
      },
      get accessoryCount() {
        return this.getIncludedQuantity(this.accessoryItems);
      },
      get selectedDevice() {
        // Check kit for selected device type
        const deviceTypes = ['tablet', 'laptop', 'other_device'];
        for (const type of deviceTypes) {
          const deviceKit = this.kit[type];
          if (deviceKit && deviceKit.products && deviceKit.products.length > 0) {
            // Find the button to get the label
            const button = this.deviceButtons.find(b => b.id === type);
            const label = button?.label || type;

            // Build the value based on device type
            let value = '';
            if (type === 'tablet') {
              value = this.tabletDisplayValue;
            } else if (type === 'other_device') {
              value = 'Other Device';
            } else {
              // For laptop
              value = this.context.laptop_style || button?.kit?.style || 'Laptop';
            }

            return { id: type, label: label.toUpperCase(), value };
          }
        }
        return null;
      },
      get productCount() {
        return this.getIncludedQuantity(this.cartItems);
      },
      get totalPrice() {
        const total = this.availableCartItems.reduce((sum, item) => {
          const price = parseFloat(item.product?.price || 0) / 100;
          return sum + (price * item.quantity);
        }, 0);
        return '$' + total.toFixed(2);
      },
      get hasProductWarning() {
        if (this.cartItems.length === 0) return false;
        return this.cartItems.some(item => {
          const product = item.product;
          if (!product) return false;
          if (!product.available) return true;
          if (!product.price || product.price === 0) return true;
          return false;
        });
      },
      get notifyProduct() {
        if (!this.modal.handle) return null;
        return this.products[this.modal.handle] || null;
      },

      getCachedProductByHandle(handle) {
        if (!handle || !this.products || typeof this.products !== 'object') return null;
        if (this.products[handle]) return this.products[handle];

        const token = this.normalizeSummaryProductToken(handle);
        if (!token) return null;

        const matchedKey = Object.keys(this.products).find(key => this.normalizeSummaryProductToken(key) === token);
        return matchedKey ? this.products[matchedKey] : null;
      },

      normalizeSummaryProductToken(value) {
        if (value === null || value === undefined) return '';
        return String(value).trim().toLowerCase();
      },
    };
  });
});
